import React, { useState, useRef, useEffect, useCallback } from "react";
import { Check, X as CloseIcon, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import useIsMobile from "@/hooks/useIsMobile";

/**
 * EditableField — the core "click-to-edit / tap-to-edit, blur-to-save" primitive.
 * Replaces toggle-modal edit flows across every detail page.
 *
 * Variants: text | textarea | number | select | date | email | url
 *
 * Save rules:
 *   - Desktop: click shows input. Enter commits. Escape cancels. Blur commits if changed.
 *   - Mobile: tap shows input (native keyboard). Done (keyboard) commits. Swipe-off commits.
 *
 * Props:
 *   value       — current value
 *   onSave      — async (newVal) => any. Called only if value changed.
 *   type        — "text" | "textarea" | "number" | "select" | "date" | "email" | "url"
 *   options     — for select: [{value, label}]
 *   placeholder — shown when value is empty
 *   displayFormatter — (value) => ReactNode (e.g. format date, look up user name)
 *   validate    — (newVal) => string | null  (error message or null)
 *   readOnly    — disable editing
 *   multiline   — textarea grows with content
 *   label       — optional floating label (rendered above field in read mode)
 *   className   — applied to the display span
 *   inputClassName — applied to the input element
 *   emptyText   — display shown when value is falsy ("Set", "—", etc.)
 *
 * Examples:
 *   <EditableField value={task.title} onSave={(v) => saveTask({title:v})} />
 *   <EditableField type="textarea" value={brief.description} onSave={...} />
 *   <EditableField type="select" options={STATUS_OPTS} value={task.status} onSave={...} />
 *   <EditableField type="date" value={task.due_date} onSave={...} displayFormatter={fmtDate} />
 */
export function EditableField({
  value,
  onSave,
  type = "text",
  options = [],
  placeholder = "",
  displayFormatter,
  validate,
  readOnly = false,
  multiline = false,
  label,
  className,
  inputClassName,
  emptyText = "Set",
  size = "md", // "sm" | "md" | "lg"
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        try { inputRef.current.select(); } catch (_) { /* ignore for non-text inputs */ }
      }
    }
  }, [editing]);

  const commit = useCallback(async (next) => {
    const nextVal = next === undefined ? draft : next;
    // No change — just close
    if (nextVal === (value ?? "")) {
      setEditing(false);
      setError(null);
      return;
    }
    if (validate) {
      const errMsg = validate(nextVal);
      if (errMsg) {
        setError(errMsg);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(nextVal);
      setEditing(false);
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    } catch (e) {
      console.error("EditableField save failed", e);
      setError(e?.response?.data?.detail || e?.message || "Save failed");
      // Keep editing open so user can retry
    } finally {
      setSaving(false);
    }
  }, [draft, value, validate, onSave]);

  const cancel = useCallback(() => {
    setDraft(value ?? "");
    setEditing(false);
    setError(null);
  }, [value]);

  // Keyboard handling in input
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter" && !e.shiftKey && type !== "textarea") {
      e.preventDefault();
      commit();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  }, [cancel, commit, type]);

  const displayValue = value == null || value === ""
    ? null
    : (displayFormatter ? displayFormatter(value) : String(value));

  const sizeClass = {
    sm: "text-xs py-1 px-2",
    md: "text-sm py-1.5 px-2.5",
    lg: "text-base py-2 px-3",
  }[size] || "text-sm py-1.5 px-2.5";

  // ──────────────── Read mode ────────────────
  if (!editing) {
    const display = (
      <span
        onClick={() => !readOnly && setEditing(true)}
        className={cn(
          "inline-flex items-center gap-1.5 group",
          !readOnly && "cursor-pointer hover:text-blue-400 transition-colors",
          readOnly && "cursor-default",
          isMobile && !readOnly && "min-h-[44px] py-2", // 44px touch target on mobile
          className
        )}
        role={!readOnly ? "button" : undefined}
        tabIndex={!readOnly ? 0 : undefined}
        onKeyDown={(e) => {
          if (!readOnly && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        {displayValue ?? (
          <span className="text-[var(--t3)] italic">{placeholder || emptyText}</span>
        )}
        {!readOnly && !isMobile && (
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 text-blue-400 transition-opacity" />
        )}
      </span>
    );
    if (label) {
      return (
        <div>
          <div className="text-xs text-[var(--t3)] font-medium mb-1">{label}</div>
          {display}
        </div>
      );
    }
    return display;
  }

  // ──────────────── Edit mode ────────────────
  const baseInput = cn(
    "flex-1 bg-[var(--s1)] border border-blue-500/50 rounded text-[var(--t1)]",
    "focus:outline-none focus:ring-2 focus:ring-blue-500/60",
    "disabled:opacity-60",
    sizeClass,
    error && "border-red-500/70 focus:ring-red-500/40",
    inputClassName
  );

  let input;
  if (type === "textarea" || multiline) {
    input = (
      <textarea
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit()}
        disabled={saving}
        rows={3}
        placeholder={placeholder}
        className={cn(baseInput, "resize-y min-h-[70px]")}
      />
    );
  } else if (type === "select") {
    input = (
      <select
        ref={inputRef}
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          // selects commit immediately
          commit(v);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => commit()}
        disabled={saving}
        className={baseInput}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  } else if (type === "date") {
    input = (
      <input
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit()}
        disabled={saving}
        className={baseInput}
      />
    );
  } else if (type === "number") {
    input = (
      <input
        ref={inputRef}
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit()}
        disabled={saving}
        placeholder={placeholder}
        className={baseInput}
      />
    );
  } else {
    input = (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit()}
        disabled={saving}
        placeholder={placeholder}
        className={baseInput}
      />
    );
  }

  const editRow = (
    <div className="flex items-center gap-1.5 w-full">
      {input}
      {/* Inline action buttons — hidden on desktop by default (Enter/Esc do the work); shown on mobile */}
      {isMobile && (
        <>
          <button
            type="button"
            onClick={() => commit()}
            disabled={saving}
            className="p-1.5 rounded bg-blue-600 text-white disabled:opacity-50"
            aria-label="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="p-1.5 rounded text-[var(--t3)]"
            aria-label="Cancel"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );

  const content = (
    <div>
      {editRow}
      {error && <div className="text-xs text-red-400 mt-1">{error}</div>}
      {!isMobile && !error && (
        <div className="text-[10px] text-[var(--t3)] mt-1 opacity-70">
          Enter to save · Esc to cancel
        </div>
      )}
    </div>
  );

  if (label) {
    return (
      <div>
        <div className="text-xs text-[var(--t3)] font-medium mb-1">{label}</div>
        {content}
      </div>
    );
  }
  return content;
}

export default EditableField;
